import { Fragment, ReactElement, useEffect, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "./icons";
import { classNames } from "../utils";
import ReactTooltip from "react-tooltip";
import InputField from "./InputField";

export type DropdownItem = { id: string; label: string };
type Props = {
  items: DropdownItem[];
  onChange?: (selected: DropdownItem) => void;
  name: string;
  label?: string;
  initial?: DropdownItem;
  labelClassName?: string;
  buttonContainerClassName?: string;
  containerClassName?: string;
  placeHolder?: string;
  disabled?: boolean;
  needTypeAhead?: boolean;
  isValid?: boolean;
  icon?: ReactElement | null;
  tooltip?: string | null;
  html?: boolean;
};

export default function DropdownCombo({
  items,
  onChange,
  label,
  name,
  initial,
  labelClassName,
  buttonContainerClassName,
  containerClassName,
  placeHolder,
  disabled = false,
  isValid = true,
  icon = null,
  tooltip = null,
  html = false,
  needTypeAhead = false,
}: Props) {
  const [selected, setSelected] = useState<DropdownItem>();
  const [typeAhead, setTypeAhead] = useState<string>("");

  useEffect(() => {
    if (initial && !(selected && selected.id !== "")) {
      setSelected(initial);
      setTypeAhead(initial.label);
      onChange?.(initial);
    } else if (initial && initial.id !== selected?.id) {
      setSelected({ id: "", label: "" });
    }
  }, [initial, onChange, selected]);

  useEffect(() => {
    if (selected?.id === "") setTypeAhead("");
  }, [items, selected?.id]);

  function handleChange(selectedItem: DropdownItem) {
    setTypeAhead(selectedItem.label);
    setSelected(selectedItem);
    onChange?.(selectedItem);
  }

  return (
    <div className={classNames("relative w-full", containerClassName ?? "")}>
      <div className="flex gap-1">
        {label && (
          <label
            htmlFor={name}
            className={classNames(
              "block mb-2 text-sm font-medium text-gray-600",
              labelClassName ?? ""
            )}
          >
            {label}
          </label>
        )}
        {tooltip && icon && (
          <>
            <span data-tip data-for={`${name}-tooltip`}>
              {icon}
            </span>
            <ReactTooltip
              className="hover:visible:opacity-1 w-96 mr-auto"
              delayHide={300}
              effect="solid"
              place="right"
              html={html}
              id={`${name}-tooltip`}
            >
              {tooltip}
            </ReactTooltip>
          </>
        )}
      </div>
      <Combobox
        disabled={disabled}
        value={selected ?? ""}
        onChange={handleChange}
      >
        <div className="w-full">
          <div
            className={`${
              buttonContainerClassName ?? ""
            } relative w-96 p-2 pl-1 cursor-default overflow-hidden rounded-lg border ${
              isValid ? "" : "border-rose-600"
            }  bg-white text-left shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-300 sm:text-sm`}
          >
            <Combobox.Button className="relative inset-y-0 right-0 flex justify-between px-2.5 w-full items-center">
              {needTypeAhead && (
                <InputField
                  handleChange={(e) => {
                    setTypeAhead(e.target.value);
                  }}
                  name={`typeAhead`}
                  placeholder={placeHolder}
                  autoComplete={"off"}
                  value={typeAhead}
                  className="p-0 w-full"
                  classes=" p-0 w-full"
                  disabled={disabled}
                />
              )}

              {!needTypeAhead && (
                <>
                  <span className="font-medium text-gray-600 w-30">
                    {selected?.label}
                  </span>
                  {!selected ? (
                    <span className="flex-grow text-left text-gray-400">
                      {placeHolder}
                    </span>
                  ) : null}
                  {}
                </>
              )}
              {!disabled ? <ChevronDownIcon aria-hidden="true" /> : null}
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-50">
              {items
                .filter((item) =>
                  selected?.label === typeAhead
                    ? true
                    : item.label.toLowerCase().includes(typeAhead.toLowerCase())
                )
                .map((item) => (
                  <Combobox.Option
                    key={item.id}
                    className={({ active }) =>
                      `flex cursor-pointer select-none py-2 pl-10 pr-4 ${
                        active ? "bg-gray-600 text-white" : "text-gray-900"
                      }`
                    }
                    value={item}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={`block truncate ${
                            selected ? "font-medium" : "font-normal"
                          }`}
                        >
                          {item.label}
                        </span>
                        {selected ? (
                          <span
                            className={`inset-y-0 left-0 flex items-center pl-3 ${
                              active ? "text-white" : "text-gray-600"
                            }`}
                          >
                            <CheckIcon aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
}
